import { useCallback, useEffect, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import { angkasaRound, clipFor as msClip, enClipFor, type AngkasaQ } from "@jomcerdik/shared";
import { getLang, t } from "../../../lib/i18n";
import { playClip } from "../../../lib/bacaAudio";
import { Screen, StudyGate } from "../kit";
import { Feedback, GameHeader, Intro, Result, YearChips, useYear } from "./common";

/* Space Math: the question is on top, every answer is a space rock falling towards the ship.
   Move the fighter under the rock with the right answer, then press Tembak to fire green plasma bolts. A wrong rock that is hit (or any rock that reaches the ship) crashes into the ship. */

const ROUND = 10;
const LIVES = 3;
const W = 360, H = 520; // logical size; the canvas is scaled to the screen
const SHIP_Y = H - 56;
const LANES = 4;

type Rock = {
  lane: number; x: number; y: number; r: number; text: string; ok: boolean; rot: number; vr: number; shape: number[];
  state: "fall" | "crash" | "boom" | "fade" | "glow";
};
type Spark = { x: number; y: number; vx: number; vy: number; life: number; max: number; color: string; size: number };
type Bolt = { x: number; y: number; vx: number; vy: number };
type Ring = { x: number; y: number; t: number; color: string; big?: boolean };
type Puff = { x: number; y: number; vx: number; vy: number; life: number; max: number; r: number; dark: number };
type Game = {
  qs: AngkasaQ[]; n: number; score: number; lives: number; rocks: Rock[]; sparks: Spark[]; bolts: Bolt[]; rings: Ring[]; smoke: Puff[]; destroyed: boolean; fx: number;
  shipX: number; targetX: number; shake: number; flash: number; shield: number; speed: number; locked: boolean; over: boolean;
  down: boolean; shots: number; cool: number; volleys: number; streak: { x: number; y: number; t: number } | null;
  next: number; answered: boolean;
};

const rnd = (a: number, b: number) => a + Math.random() * (b - a);
const isEn = () => getLang() !== "ms";
const RIGHT = () => (isEn() ? "Right! Well done!" : "Betul! Pandai!");
const TRY = () => (isEn() ? "Try again." : "Cuba lagi.");
const sayLine = (text: string) => { try { playClip(isEn() ? enClipFor("line", text) : msClip("line", text), text, 0.9); } catch { /* the clip is optional */ } };

/** Tiny synthesised sound effects (no files). Created on the first tap, silent if the browser refuses. */
function useSfx() {
  const ctx = useRef<AudioContext | null>(null);
  useEffect(() => () => { try { void ctx.current?.close(); } catch { /* ignore */ } }, []);
  return useCallback((freq: number, dur: number, type: OscillatorType = "sine", slide = 0) => {
    try {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      ctx.current ??= new AC();
      const c = ctx.current; const o = c.createOscillator(); const g = c.createGain();
      o.type = type; o.frequency.setValueAtTime(freq, c.currentTime);
      if (slide) o.frequency.exponentialRampToValueAtTime(Math.max(30, freq + slide), c.currentTime + dur);
      g.gain.setValueAtTime(0.12, c.currentTime); g.gain.exponentialRampToValueAtTime(0.001, c.currentTime + dur);
      o.connect(g); g.connect(c.destination); o.start(); o.stop(c.currentTime + dur);
    } catch { /* sound is a bonus */ }
  }, []);
}

const reducedMotion = () => { try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; } };

export function AngkasaPage() { return <Screen><StudyGate><Angkasa /></StudyGate></Screen>; }

function Angkasa() {
  const [year, setYear] = useYear();
  const [started, setStarted] = useState(false);
  const [done, setDone] = useState<{ score: number } | null>(null);
  const [seed, setSeed] = useState(0);
  const [hud, setHud] = useState({ n: 0, score: 0, lives: LIVES });
  const header = <GameHeader title={t("Space Math")} emoji="☄️" tone="from-indigo-600 to-slate-800" score={started ? hud.score : undefined} lives={started ? hud.lives : undefined} progress={started ? hud.n / ROUND : undefined} />;
  if (!started) return <>{header}<Intro emoji="☄️" title={t("Space Math")} text={t("Gerakkan kapal, kemudian tekan Tembak pada batu angkasa yang ada jawapan betul. Jika tembak yang salah, batu itu akan hentam kapal!")} onStart={() => { setStarted(true); setDone(null); setHud({ n: 0, score: 0, lives: LIVES }); setSeed((s) => s + 1); }}><YearChips year={year} onYear={setYear} /></Intro></>;
  if (done) return <>{header}<Result game="angkasa" score={done.score} max={ROUND} onAgain={() => { setStarted(false); setDone(null); }} details={{ year }} /></>;
  return <>{header}<Arena key={seed} year={year} onHud={setHud} onEnd={(score) => setDone({ score })} /></>;
}

/** One rock per answer, one lane each, starting at different heights above the screen. */
function newRocks(q: AngkasaQ): Rock[] {
  const laneW = W / LANES, r = Math.max(26, Math.min(46, laneW * 0.42));
  return q.options.map((text, lane): Rock => ({
    lane, x: laneW * (lane + 0.5), y: -r - rnd(0, 150), r, text, ok: text === q.answer, rot: rnd(0, 6.28), vr: rnd(-1, 1),
    shape: Array.from({ length: 9 }, () => rnd(0.82, 1.1)), state: "fall",
  }));
}

/** The deep-space backdrop is painted once (nebula clouds, planets) and scrolled slowly. */
function makeBackdrop(): HTMLCanvasElement {
  const cv = document.createElement("canvas"); cv.width = W; cv.height = H * 2;
  const c = cv.getContext("2d")!;
  const base = c.createLinearGradient(0, 0, 0, H * 2); base.addColorStop(0, "#0b1030"); base.addColorStop(0.5, "#2a0f5c"); base.addColorStop(1, "#0b1030");
  c.fillStyle = base; c.fillRect(0, 0, W, H * 2);
  const blobs: [number, number, number, string][] = [[70, 250, 190, "rgba(168,85,247,.55)"], [300, 430, 210, "rgba(59,130,246,.45)"], [120, 620, 200, "rgba(236,72,153,.38)"], [290, 800, 200, "rgba(99,102,241,.5)"], [60, 880, 150, "rgba(34,211,238,.28)"]];
  for (const [x, y, r, col] of blobs) { const g = c.createRadialGradient(x, y, 0, x, y, r); g.addColorStop(0, col); g.addColorStop(1, "rgba(0,0,0,0)"); c.fillStyle = g; c.fillRect(0, 0, W, H * 2); }
  const planet = (x: number, y: number, r: number, a: string, b: string, ring: boolean) => {
    const g = c.createRadialGradient(x - r * 0.35, y - r * 0.35, r * 0.1, x, y, r); g.addColorStop(0, a); g.addColorStop(1, b);
    if (ring) { c.strokeStyle = "rgba(233,213,255,.55)"; c.lineWidth = 5; c.beginPath(); c.ellipse(x, y, r * 1.7, r * 0.32, -0.35, Math.PI, Math.PI * 2); c.stroke(); }
    c.fillStyle = g; c.beginPath(); c.arc(x, y, r, 0, 7); c.fill();
    if (ring) { c.strokeStyle = "rgba(233,213,255,.7)"; c.lineWidth = 5; c.beginPath(); c.ellipse(x, y, r * 1.7, r * 0.32, -0.35, 0, Math.PI); c.stroke(); }
  };
  planet(300, 160, 44, "#c4b5fd", "#4c1d95", true);
  planet(40, 640, 70, "#f0abfc", "#701a75", false);
  planet(310, 860, 36, "#7dd3fc", "#1e3a8a", false);
  return cv;
}

function Arena({ year, onHud, onEnd }: { year: number; onHud: (h: { n: number; score: number; lives: number }) => void; onEnd: (score: number) => void }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const game = useRef<Game | null>(null);
  const [q, setQ] = useState<AngkasaQ | null>(null);
  const [verdict, setVerdict] = useState<"ok" | "no" | null>(null);
  const [rightText, setRightText] = useState("");
  const [num, setNum] = useState(1);
  const sfx = useSfx();
  const sfxRef = useRef(sfx); sfxRef.current = sfx;
  const onHudRef = useRef(onHud); onHudRef.current = onHud;
  const onEndRef = useRef(onEnd); onEndRef.current = onEnd;

  const startQuestion = useCallback((g: Game) => {
    const cur = g.qs[g.n]!;
    g.rocks = newRocks(cur); setNum(g.n + 1); g.locked = false; g.answered = false; g.bolts = [];
    setQ(cur); setVerdict(null); setRightText(cur.answer);
    onHudRef.current({ n: g.n, score: g.score, lives: g.lives });
  }, []);

  const finishQuestion = useCallback((g: Game, ok: boolean) => {
    if (g.answered) return;
    g.answered = true; g.locked = true;
    if (ok) g.score += 1; else g.lives -= 1;
    setVerdict(ok ? "ok" : "no");
    sayLine(ok ? RIGHT() : TRY());
    for (const r of g.rocks) if (r.state === "fall") r.state = r.ok ? (ok ? "boom" : "glow") : "fade";
    onHudRef.current({ n: g.n + 1, score: g.score, lives: Math.max(0, g.lives) });
    g.next = performance.now() + (ok ? 900 : 1900);
  }, []);

  const explode = (g: Game, x: number, y: number, colors: string[], n: number) => {
    for (let i = 0; i < n; i++) { const a = rnd(0, 6.28), v = rnd(40, 240); g.sparks.push({ x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, life: 0, max: rnd(0.4, 0.95), color: colors[i % colors.length]!, size: rnd(2, 5) }); }
    g.rings.push({ x, y, t: 0, color: colors[0]! });
  };

  /** Move the ship under a lane (keyboard 1-4 and the screen-reader buttons). It never fires by itself. */
  const aim = useCallback((lane: number) => {
    const g = game.current; if (!g || g.locked || g.over) return;
    g.targetX = (W / LANES) * (lane + 0.5);
  }, []);
  /** One volley per press of the Tembak button (or Space). */
  const shoot = useCallback(() => {
    const g = game.current; if (!g || g.locked || g.over) return;
    g.shots = Math.min(2, g.shots + 1);
  }, []);

  useEffect(() => {
    const cv = canvas.current; if (!cv) return;
    const ctx = cv.getContext("2d"); if (!ctx) return;
    const calm = reducedMotion();
    const backdrop = makeBackdrop();
    const g: Game = {
      qs: angkasaRound(year, ROUND), n: 0, score: 0, lives: LIVES, rocks: [], sparks: [], bolts: [], rings: [], smoke: [], destroyed: false, fx: 0, shipX: W / 2, targetX: W / 2, shake: 0, flash: 0, shield: 0,
      speed: (34 + year * 8) * (calm ? 0.7 : 1), locked: false, over: false, down: false, shots: 0, cool: 0, volleys: 0, streak: null, next: 0, answered: false,
    };
    const stars = Array.from({ length: 90 }, () => ({ x: rnd(0, W), y: rnd(0, H), s: rnd(0.6, 2.2), v: rnd(10, 60), tw: rnd(0, 6) }));
    game.current = g; startQuestion(g);

    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const fit = () => { const w = cv.clientWidth; cv.width = Math.round(w * dpr); cv.height = Math.round((w * H / W) * dpr); };
    fit();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(fit) : null; ro?.observe(cv);

    let raf = 0, last = performance.now(), scroll = 0;
    const loop = (now: number) => {
      const dt = Math.min(0.05, (now - last) / 1000); last = now;
      step(dt, now); draw(now);
      raf = requestAnimationFrame(loop);
    };

    function volley() {
      // Five green plasma bolts in a narrow fan: it only reaches the rock straight above the ship.
      const spread = [-0.04, -0.02, 0, 0.02, 0.04], off = [-11, -5.5, 0, 5.5, 11];
      spread.forEach((a, i) => g.bolts.push({ x: g.shipX + off[i]!, y: SHIP_Y - 38, vx: Math.sin(a) * 760, vy: -Math.cos(a) * 760 }));
      if (g.volleys++ % 2 === 0) sfxRef.current(760, 0.07, "square", -380);
    }

    function step(dt: number, now: number) {
      scroll = (scroll + dt * 18) % (H * 2);
      for (const s of stars) { s.y += s.v * dt; if (s.y > H) { s.y = 0; s.x = rnd(0, W); } }
      if (!g.streak && Math.random() < dt * 0.25) g.streak = { x: rnd(60, W), y: rnd(0, 140), t: 0 };
      if (g.streak) { g.streak.t += dt; if (g.streak.t > 0.6) g.streak = null; }
      // The ship only moves here; it fires when the player presses Tembak.
      const dx = g.targetX - g.shipX;
      g.shipX += Math.sign(dx) * Math.min(Math.abs(dx), 900 * dt);
      g.shake = Math.max(0, g.shake - dt * 14); g.flash = Math.max(0, g.flash - dt * 2.4); g.shield = Math.max(0, g.shield - dt * 2);
      g.cool -= dt;
      if (g.shots > 0 && g.cool <= 0) { if (!g.locked && !g.over) volley(); g.shots -= 1; g.cool = 0.16; }
      for (const b of g.bolts) { b.x += b.vx * dt; b.y += b.vy * dt; }
      const hurt = LIVES - Math.max(0, g.lives);
      g.fx -= dt;
      if (!g.destroyed && g.fx <= 0 && hurt >= 1) {
        g.fx = hurt >= 2 ? 0.045 : 0.7;
        if (hurt >= 2) g.smoke.push({ x: g.shipX + rnd(-12, 12), y: SHIP_Y + rnd(-18, 8), vx: rnd(-18, 18), vy: rnd(-34, 22), life: 0, max: rnd(0.9, 1.6), r: rnd(6, 11), dark: rnd(0.25, 0.7) });
        else g.sparks.push(...Array.from({ length: 3 }, () => ({ x: g.shipX - 24, y: SHIP_Y + 4, vx: rnd(-50, 50), vy: rnd(-70, -10), life: 0, max: rnd(0.25, 0.5), color: "#fde047", size: 2.5 })));
      }
      for (const m of g.smoke) { m.life += dt; m.x += m.vx * dt; m.y += m.vy * dt; m.r += 9 * dt; }
      g.smoke = g.smoke.filter((m) => m.life < m.max);
      for (const s of g.sparks) { s.life += dt; s.x += s.vx * dt; s.y += s.vy * dt; s.vy += 120 * dt; }
      g.sparks = g.sparks.filter((s) => s.life < s.max);
      for (const rg of g.rings) rg.t += dt;
      g.rings = g.rings.filter((rg) => rg.t < 0.5);
      for (const r of g.rocks) {
        r.rot += r.vr * dt;
        if (r.state === "fall" && !g.locked) {
          r.y += g.speed * dt;
          if (r.y + r.r >= SHIP_Y - 18) { r.state = "crash"; g.locked = true; }
        } else if (r.state === "fall") r.y += g.speed * dt * 0.2;
        if (r.state === "crash") {
          const ddx = g.shipX - r.x, ddy = SHIP_Y - r.y, d = Math.hypot(ddx, ddy) || 1, v = 520;
          r.x += (ddx / d) * v * dt; r.y += (ddy / d) * v * dt;
          if (d < r.r + 14) {
            r.state = "boom"; explode(g, g.shipX, SHIP_Y - 6, ["#ef4444", "#f97316", "#fde047"], 34);
            g.shake = 1; g.flash = 1; g.shield = 1; sfxRef.current(110, 0.5, "sawtooth", -60);
            finishQuestion(g, false);
            if (g.lives <= 0) {
              // third hit: the ship blows up (meletup)
              g.destroyed = true; g.shake = 2.2; g.next = performance.now() + 2600;
              explode(g, g.shipX, SHIP_Y - 10, ["#fef08a", "#fb923c", "#ef4444", "#f8fafc"], 90);
              g.rings.push({ x: g.shipX, y: SHIP_Y - 10, t: 0, color: "#fde047", big: true }, { x: g.shipX, y: SHIP_Y - 10, t: 0.12, color: "#f97316", big: true });
              sfxRef.current(70, 0.9, "sawtooth", -40); setTimeout(() => sfxRef.current(140, 0.6, "square", -100), 120);
            }
          }
        }
      }
      // bolts against rocks: the first rock hit decides the question
      for (const b of g.bolts) {
        if (g.locked) break;
        const rock = g.rocks.find((r) => r.state === "fall" && Math.hypot(r.x - b.x, r.y - b.y) < r.r * 0.9);
        if (!rock) continue;
        b.y = -999;
        if (rock.ok) {
          rock.state = "boom"; explode(g, rock.x, rock.y, ["#fde047", "#fb923c", "#f8fafc", "#4ade80"], 34); sfxRef.current(180, 0.35, "sawtooth", -120);
          finishQuestion(g, true);
        } else { rock.state = "crash"; g.locked = true; sfxRef.current(300, 0.2, "triangle", -200); }
      }
      g.bolts = g.bolts.filter((b) => b.y > -20);
      g.rocks = g.rocks.filter((r) => r.state !== "boom");
      if (g.answered && now >= g.next && !g.over) {
        if (g.lives <= 0 || g.n + 1 >= ROUND) { g.over = true; onEndRef.current(g.score); return; }
        g.n += 1; startQuestion(g);
      }
    }

    function rockPath(c: CanvasRenderingContext2D, r: Rock) {
      c.beginPath();
      r.shape.forEach((k, i) => { const a = (i / r.shape.length) * Math.PI * 2 + r.rot; const x = r.x + Math.cos(a) * r.r * k, y = r.y + Math.sin(a) * r.r * k; if (i) c.lineTo(x, y); else c.moveTo(x, y); });
      c.closePath();
    }

    function drawShip(c: CanvasRenderingContext2D, firing: boolean) {
      if (g.destroyed) return;
      const hurt = LIVES - Math.max(0, g.lives);
      c.save(); c.translate(g.shipX, SHIP_Y); c.scale(1.12, 1.12);
      if (hurt >= 2) c.rotate(Math.sin(performance.now() / 160) * 0.06); // limping
      // engine flames
      for (const sx of [-11, 11]) {
        if (hurt >= 2 && sx === -11 && Math.random() < 0.45) continue; // the damaged engine sputters
        const len = 14 + Math.random() * 12;
        const fl = c.createLinearGradient(0, 24, 0, 24 + len); fl.addColorStop(0, "#fff7ed"); fl.addColorStop(0.35, "#fbbf24"); fl.addColorStop(1, "rgba(239,68,68,0)");
        c.fillStyle = fl; c.shadowColor = "#fb923c"; c.shadowBlur = 16;
        c.beginPath(); c.moveTo(sx - 5, 24); c.lineTo(sx, 24 + len); c.lineTo(sx + 5, 24); c.fill();
      }
      c.shadowBlur = firing ? 18 : 8; c.shadowColor = "#4ade80";
      // swept wings
      for (const sd of [-1, 1]) {
        c.save(); c.scale(sd, 1);
        const wg = c.createLinearGradient(0, -10, 36, 26); wg.addColorStop(0, "#22c55e"); wg.addColorStop(1, "#14532d");
        c.fillStyle = wg; c.strokeStyle = "#bbf7d0"; c.lineWidth = 1.6;
        const torn = hurt >= 2 && sd === -1; // the left wing tip is torn off after the second hit
        c.beginPath();
        if (torn) { c.moveTo(7, -8); c.lineTo(24, 6); c.lineTo(19, 11); c.lineTo(26, 17); c.lineTo(20, 24); c.lineTo(8, 20); } else { c.moveTo(7, -8); c.lineTo(36, 14); c.lineTo(38, 27); c.lineTo(26, 24); c.lineTo(8, 20); }
        c.closePath(); c.fill(); c.stroke();
        if (!torn) {
          c.fillStyle = "#e2e8f0"; c.fillRect(30, 2, 5, 17); // wing cannon
          c.fillStyle = hurt >= 1 && sd === 1 ? "#7f1d1d" : "#4ade80"; c.beginPath(); c.arc(32.5, 0, 3, 0, 7); c.fill();
        }
        if (hurt >= 1) { // cracks and burn marks
          c.strokeStyle = "rgba(17,24,39,.85)"; c.lineWidth = 1.3; c.beginPath(); c.moveTo(16, -1); c.lineTo(21, 6); c.lineTo(18, 10); c.lineTo(24, 16); c.stroke();
          c.fillStyle = "rgba(0,0,0,.35)"; c.beginPath(); c.ellipse(20, 14, 6, 4, 0.5, 0, 7); c.fill();
        }
        c.restore();
      }
      c.shadowBlur = 6;
      // fuselage
      const fu = c.createLinearGradient(-12, 0, 12, 0); fu.addColorStop(0, "#94a3b8"); fu.addColorStop(0.5, "#f8fafc"); fu.addColorStop(1, "#94a3b8");
      c.fillStyle = fu; c.strokeStyle = "#475569"; c.lineWidth = 1.6;
      c.beginPath(); c.moveTo(0, -42); c.bezierCurveTo(10, -28, 12, -6, 10, 22); c.lineTo(5, 30); c.lineTo(-5, 30); c.lineTo(-10, 22); c.bezierCurveTo(-12, -6, -10, -28, 0, -42); c.fill(); c.stroke();
      c.shadowBlur = 0;
      c.strokeStyle = "#22c55e"; c.lineWidth = 2.4; c.beginPath(); c.moveTo(-6, 6); c.lineTo(-6, 24); c.moveTo(6, 6); c.lineTo(6, 24); c.stroke();
      // cockpit
      const ck = c.createLinearGradient(0, -24, 0, 0); ck.addColorStop(0, "#e0f2fe"); ck.addColorStop(1, "#0284c7");
      c.fillStyle = ck; c.beginPath(); c.ellipse(0, -13, 5.5, 10, 0, 0, 7); c.fill(); c.strokeStyle = "#0c4a6e"; c.lineWidth = 1.2; c.stroke();
      c.fillStyle = "rgba(255,255,255,.7)"; c.beginPath(); c.ellipse(-1.8, -16, 1.6, 4, 0, 0, 7); c.fill();
      if (hurt >= 1) { // scorch marks and a dent on the nose
        const sc = c.createRadialGradient(4, 6, 1, 4, 6, 12); sc.addColorStop(0, "rgba(0,0,0,.55)"); sc.addColorStop(1, "rgba(0,0,0,0)");
        c.fillStyle = sc; c.fillRect(-12, -6, 24, 26);
        c.strokeStyle = "rgba(17,24,39,.8)"; c.lineWidth = 1.2; c.beginPath(); c.moveTo(-5, -30); c.lineTo(-2, -24); c.lineTo(-6, -20); c.stroke();
      }
      if (hurt >= 2) { // the canopy is cracked and glowing hot
        c.strokeStyle = "#111827"; c.lineWidth = 1.4; c.beginPath(); c.moveTo(-4, -20); c.lineTo(1, -12); c.lineTo(-2, -5); c.moveTo(1, -12); c.lineTo(6, -9); c.stroke();
        c.fillStyle = "rgba(0,0,0,.45)"; c.fillRect(-11, 4, 22, 24);
        c.fillStyle = `rgba(251,113,33,${0.35 + 0.35 * Math.sin(performance.now() / 90)})`; c.beginPath(); c.arc(-4, 14, 4, 0, 7); c.fill();
      }
      c.restore();
      if (g.shield > 0) { c.strokeStyle = `rgba(248,113,113,${g.shield})`; c.lineWidth = 4; c.shadowColor = "#ef4444"; c.shadowBlur = 16; c.beginPath(); c.arc(g.shipX, SHIP_Y - 4, 48, 0, 7); c.stroke(); c.shadowBlur = 0; }
    }

    function draw(now: number) {
      const c = ctx!; const k = cv!.width / W;
      c.setTransform(k, 0, 0, k, 0, 0);
      const sx = g.shake ? rnd(-6, 6) * g.shake : 0, sy = g.shake ? rnd(-6, 6) * g.shake : 0;
      c.save(); c.translate(sx, sy);
      c.drawImage(backdrop, 0, scroll - H * 2); c.drawImage(backdrop, 0, scroll);
      for (const s of stars) { c.globalAlpha = 0.45 + 0.55 * Math.sin(s.tw + now / 420 + s.x); c.fillStyle = s.s > 1.6 ? "#e0f2fe" : "#fff"; c.fillRect(s.x, s.y, s.s, s.s * (s.v > 40 ? 2.4 : 1)); }
      c.globalAlpha = 1;
      if (g.streak) { const a = 1 - g.streak.t / 0.6; const gr = c.createLinearGradient(g.streak.x, g.streak.y, g.streak.x - 70, g.streak.y - 40); gr.addColorStop(0, `rgba(255,255,255,${a})`); gr.addColorStop(1, "rgba(255,255,255,0)"); c.strokeStyle = gr; c.lineWidth = 2; c.beginPath(); c.moveTo(g.streak.x - g.streak.t * 300, g.streak.y + g.streak.t * 170); c.lineTo(g.streak.x - g.streak.t * 300 - 70, g.streak.y + g.streak.t * 170 - 40); c.stroke(); }

      // rocks
      for (const r of g.rocks) {
        c.save();
        c.globalAlpha = r.state === "fade" ? 0.25 : 1;
        const glowing = r.state === "glow";
        c.shadowColor = glowing ? "#4ade80" : r.state === "crash" ? "#ef4444" : "#fb923c"; c.shadowBlur = glowing ? 26 : 14;
        rockPath(c, r);
        const gr = c.createRadialGradient(r.x - r.r * 0.35, r.y - r.r * 0.35, r.r * 0.1, r.x, r.y, r.r * 1.15);
        gr.addColorStop(0, glowing ? "#bbf7d0" : "#d6d3d1"); gr.addColorStop(0.55, glowing ? "#22c55e" : "#78716c"); gr.addColorStop(1, glowing ? "#14532d" : "#292524");
        c.fillStyle = gr; c.fill(); c.shadowBlur = 0;
        c.lineWidth = glowing ? 5 : 3; c.strokeStyle = glowing ? "#dcfce7" : r.state === "crash" ? "#fca5a5" : "#fdba74"; c.stroke();
        c.fillStyle = "rgba(0,0,0,.2)";
        for (let i = 0; i < 3; i++) { const a = r.rot + i * 2.1; c.beginPath(); c.arc(r.x + Math.cos(a) * r.r * 0.62, r.y + Math.sin(a) * r.r * 0.55, r.r * 0.12, 0, 7); c.fill(); }
        c.fillStyle = "rgba(15,23,42,.62)"; c.beginPath(); c.arc(r.x, r.y, r.r * 0.66, 0, 7); c.fill();
        c.font = `900 ${Math.round(r.r * (r.text.length > 4 ? 0.52 : r.text.length > 2 ? 0.66 : 0.82))}px system-ui, sans-serif`;
        c.textAlign = "center"; c.textBaseline = "middle"; c.fillStyle = "#fff"; c.fillText(r.text, r.x, r.y + 1);
        c.restore();
      }

      // plasma bolts
      for (const b of g.bolts) {
        const a = Math.atan2(b.vx, -b.vy);
        c.save(); c.translate(b.x, b.y); c.rotate(a);
        c.shadowColor = "#4ade80"; c.shadowBlur = 12;
        c.fillStyle = "#16a34a"; c.beginPath(); c.ellipse(0, 6, 4.4, 14, 0, 0, 7); c.fill();
        c.fillStyle = "#bbf7d0"; c.beginPath(); c.ellipse(0, 2, 2.2, 9, 0, 0, 7); c.fill();
        c.restore();
      }
      c.shadowBlur = 0;

      for (const m of g.smoke) { c.globalAlpha = (1 - m.life / m.max) * 0.85; { const v = Math.round(165 - m.dark * 110); c.fillStyle = `rgb(${v},${v},${v + 10})`; } c.beginPath(); c.arc(m.x, m.y, m.r, 0, 7); c.fill(); }
      c.globalAlpha = 1;
      drawShip(c, g.cool > 0.06);
      if (g.flash > 0) { c.fillStyle = `rgba(239,68,68,${g.flash * 0.35})`; c.fillRect(-10, -10, W + 20, H + 20); }

      for (const rg of g.rings) { c.globalAlpha = Math.max(0, 1 - rg.t / 0.5); c.strokeStyle = rg.color; c.lineWidth = rg.big ? 8 : 4; c.beginPath(); c.arc(rg.x, rg.y, 10 + rg.t * (rg.big ? 420 : 160), 0, 7); c.stroke(); }
      if (g.destroyed) { // fireball
        const t = Math.max(0, 1 - (g.next - performance.now()) / 2600);
        if (t < 0.5) { const r = 20 + t * 260; const fb = c.createRadialGradient(g.shipX, SHIP_Y - 10, 0, g.shipX, SHIP_Y - 10, r); fb.addColorStop(0, `rgba(255,251,200,${0.95 - t})`); fb.addColorStop(0.4, `rgba(251,146,60,${0.8 - t})`); fb.addColorStop(1, "rgba(239,68,68,0)"); c.globalAlpha = 1; c.fillStyle = fb; c.beginPath(); c.arc(g.shipX, SHIP_Y - 10, r, 0, 7); c.fill(); }
      }
      for (const s of g.sparks) { c.globalAlpha = 1 - s.life / s.max; c.fillStyle = s.color; c.shadowColor = s.color; c.shadowBlur = 8; c.fillRect(s.x, s.y, s.size, s.size); }
      c.globalAlpha = 1; c.shadowBlur = 0;
      c.restore();
    }

    raf = requestAnimationFrame(loop);
    const key = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement | null)?.tagName === "BUTTON") return; // a focused button handles its own Space/Enter
      const n = Number(e.key);
      if (n >= 1 && n <= LANES) aim(n - 1);
      else if (e.key === " " || e.key === "Enter") { e.preventDefault(); shoot(); }
      else if (e.key === "ArrowLeft") g.targetX = Math.max(30, g.targetX - 45);
      else if (e.key === "ArrowRight") g.targetX = Math.min(W - 30, g.targetX + 45);
    };
    window.addEventListener("keydown", key);
    const release = () => { g.down = false; };
    window.addEventListener("pointerup", release); window.addEventListener("pointercancel", release);
    return () => { cancelAnimationFrame(raf); ro?.disconnect(); window.removeEventListener("keydown", key); window.removeEventListener("pointerup", release); window.removeEventListener("pointercancel", release); game.current = null; };
  }, [year, startQuestion, finishQuestion, aim, shoot]);

  const xOf = (e: RPointerEvent<HTMLCanvasElement>) => { const b = e.currentTarget.getBoundingClientRect(); return Math.max(30, Math.min(W - 30, ((e.clientX - b.left) / b.width) * W)); };
  function down(e: RPointerEvent<HTMLCanvasElement>) {
    const g = game.current; if (!g) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    g.down = true; g.targetX = xOf(e);
  }
  function move(e: RPointerEvent<HTMLCanvasElement>) { const g = game.current; if (g?.down) g.targetX = xOf(e); }

  return (
    <div className="space-y-2">
      <div className="rounded-2xl bg-slate-900 px-4 py-3 text-center text-white shadow-md">
        <p className="text-xs font-bold uppercase tracking-wide text-indigo-200">{t("Soalan")} {num}/{ROUND}</p>
        <p className="text-4xl font-black tracking-wide" aria-live="polite">{q ? `${q.text}${q.text.includes("=") ? "" : " = ?"}` : ""}</p>
      </div>
      <canvas ref={canvas} onPointerDown={down} onPointerMove={move} onContextMenu={(e) => e.preventDefault()} className="mx-auto block w-full max-w-md touch-none select-none rounded-2xl shadow-md ring-1 ring-black/10" style={{ aspectRatio: `${W} / ${H}`, WebkitTouchCallout: "none", WebkitUserSelect: "none" }} aria-label={t("Medan permainan angkasa")} />
      <div className="flex items-stretch">
        <button type="button" onContextMenu={(e) => e.preventDefault()} style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }} onPointerDown={shoot} onClick={(e) => { if (e.detail === 0) shoot(); }} disabled={verdict !== null} className="flex-1 touch-none select-none rounded-2xl bg-gradient-to-b from-rose-500 to-red-700 py-4 text-2xl font-black uppercase tracking-wider text-white shadow-lg ring-4 ring-rose-300/40 active:scale-95 disabled:opacity-50">{t("Tembak")}</button>
      </div>
      <div className="sr-only">{q?.options.map((o, i) => <button key={o} type="button" onClick={() => aim(i)}>{o}</button>)}</div>
      <Feedback state={verdict} right={rightText} />
      <p className="text-center text-xs text-slate-400">{t("Geser jari pada skrin untuk gerakkan kapal ke bawah batu angkasa yang betul, kemudian tekan Tembak.")}</p>
    </div>
  );
}
